/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { Resource, getUrl, hasLink } from '@ngxp/rest';
import { isEqual, isNull } from 'lodash-es';
import { Observable, combineLatest, filter, startWith, tap } from 'rxjs';
import { ResourceStateService } from '../ngrx/state.service';
import { isNotNull } from '../tech.util';
import { ServiceConfig } from './resource.model';
import { mapToFirst, mapToResource } from './resource.rxjs.operator';
import {
  StateResource,
  createEmptyStateResource,
  isInvalidResourceCombination,
  isLoadingRequired,
  isStateResourceStable,
} from './resource.util';

export class ResourceLoader<B extends Resource, T extends Resource> {
  configResource: B = null;

  constructor(
    private readonly config: ServiceConfig<B>,
    private readonly resourceStateService: ResourceStateService<T>,
  ) {}

  public get(): Observable<StateResource<T>> {
    return combineLatest([this.resourceStateService.selectResource(), this.getConfigResource()]).pipe(
      tap(([stateResource, configResource]) => this.handleResourceChanges(stateResource, configResource)),
      filter(([stateResource, configResource]) => !isInvalidResourceCombination(stateResource, configResource)),
      mapToFirst<T, B>(),
      startWith(createEmptyStateResource<T>(true)),
    );
  }

  private getConfigResource(): Observable<B> {
    return this.config.baseResource.pipe(filter(isStateResourceStable), mapToResource<B>());
  }

  handleResourceChanges(stateResource: StateResource<T>, configResource: B): void {
    if (!isEqual(this.configResource, configResource)) {
      this.handleConfigResourceChanges(stateResource, configResource);
    } else if (this.shouldLoadResource(stateResource, configResource)) {
      this.loadResource(configResource);
    }
  }

  handleConfigResourceChanges(stateResource: StateResource<T>, configResource: B) {
    this.configResource = configResource;
    if (isStateResourceStable(stateResource)) {
      this.updateStateResourceByConfigResource(stateResource, configResource);
    }
  }

  updateStateResourceByConfigResource(stateResource: StateResource<T>, configResource: B): void {
    if (this.shouldClearStateResource(stateResource, configResource)) {
      this.resourceStateService.clearResource();
    } else if (this.hasGetLink(configResource)) {
      this.loadResource(configResource);
    }
  }

  shouldClearStateResource(stateResource: StateResource<T>, configResource: B): boolean {
    return (isNull(configResource) || this.hasNotGetLink(configResource)) && !this.isStateResourceEmpty(stateResource);
  }

  private hasNotGetLink(configResource: B) {
    return !this.hasGetLink(configResource);
  }

  hasGetLink(configResource: B): boolean {
    return isNotNull(configResource) && hasLink(configResource, this.config.getLinkRel);
  }

  private isStateResourceEmpty(stateResource: StateResource<T>): boolean {
    return isEqual(stateResource, createEmptyStateResource());
  }

  shouldLoadResource(stateResource: StateResource<T>, configResource: B): boolean {
    return isNotNull(configResource) && hasLink(configResource, this.config.getLinkRel) && isLoadingRequired(stateResource);
  }

  loadResource(configResource: B): void {
    this.resourceStateService.loadResource(this.getGetUrl(configResource));
  }

  private getGetUrl(configResource: B): string {
    return getUrl(configResource, this.config.getLinkRel);
  }
}
